import { addDays, isoWeekday, parts, type DateOnly } from './date-only'
import type { Holiday, HolidayProvider } from './holidays'

export type DateAdjustment = 'none' | 'next_business_day' | 'previous_business_day'

/**
 * Answers "is this a working day?" and moves dates to working days.
 * Weekends + holidays from any number of providers.
 *
 * Whether a legal deadline moves when it falls on a non-working day is a
 * legal question: rules declare it explicitly (`adjust`), the calendar only
 * executes it.
 */
export class BusinessCalendar {
  private readonly cache = new Map<number, Map<DateOnly, Holiday>>()

  constructor(
    private readonly providers: HolidayProvider[] = [],
    private readonly weekendDays: ReadonlySet<number> = new Set([6, 7]),
  ) {}

  private holidayMap(year: number): Map<DateOnly, Holiday> {
    let map = this.cache.get(year)
    if (!map) {
      map = new Map()
      for (const provider of this.providers) {
        for (const h of provider.holidaysForYear(year)) map.set(h.date, h)
      }
      this.cache.set(year, map)
    }
    return map
  }

  holiday(date: DateOnly): Holiday | undefined {
    return this.holidayMap(parts(date).year).get(date)
  }

  isWeekend(date: DateOnly): boolean {
    return this.weekendDays.has(isoWeekday(date))
  }

  isBusinessDay(date: DateOnly): boolean {
    return !this.isWeekend(date) && !this.holiday(date)
  }

  nextBusinessDay(date: DateOnly): DateOnly {
    let d = date
    while (!this.isBusinessDay(d)) d = addDays(d, 1)
    return d
  }

  previousBusinessDay(date: DateOnly): DateOnly {
    let d = date
    while (!this.isBusinessDay(d)) d = addDays(d, -1)
    return d
  }

  adjust(date: DateOnly, adjustment: DateAdjustment): DateOnly {
    switch (adjustment) {
      case 'next_business_day':
        return this.nextBusinessDay(date)
      case 'previous_business_day':
        return this.previousBusinessDay(date)
      case 'none':
        return date
    }
  }

  /** Adds N business days (negative goes backwards). 0 returns the date itself. */
  addBusinessDays(date: DateOnly, days: number): DateOnly {
    const step = days >= 0 ? 1 : -1
    let remaining = Math.abs(days)
    let d = date
    while (remaining > 0) {
      d = addDays(d, step)
      if (this.isBusinessDay(d)) remaining -= 1
    }
    return d
  }

  holidaysBetween(from: DateOnly, to: DateOnly): Holiday[] {
    const out: Holiday[] = []
    for (let y = parts(from).year; y <= parts(to).year; y += 1) {
      for (const h of this.holidayMap(y).values()) if (h.date >= from && h.date <= to) out.push(h)
    }
    return out.sort((a, b) => (a.date < b.date ? -1 : 1))
  }
}
